import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { moduleDisplayName } from '../utils/moduleNames';
import { useAuth } from './AuthContext';
import { homePathFor, isAdmin, isSuperuser } from '../utils/permissions';
import { useTheme } from '../context/ThemeContext';
import Logo from './Logo';
import AIChatWidget from './AIChatWidget';
import IncomingCallWatcher from './IncomingCallWatcher';
import AiCallAlerts from './AiCallAlerts';
import NotificationBell from './NotificationBell';
import ApprovalsBadge from './approvals/ApprovalsBadge';
import GlobalSearch from './GlobalSearch';
import { SoftphoneProvider } from './softphone/SoftphoneContext';
import SoftphonePanel from './softphone/SoftphonePanel';
import LicenseGuard from './LicenseGuard';
import axios from 'axios';
import usePageTracker from '../hooks/usePageTracker';
import LanguageSwitcher from './LanguageSwitcher';
import PermissionDenied from './PermissionDenied';
import { canOpenPath } from '../utils/routeAccess';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [modulesMenuOpen, setModulesMenuOpen] = useState(false);
  const [userModules, setUserModules] = useState([]);

  // The superuser is the platform operator, not a company: no module menu,
  // no tenant widgets (softphone, AI assistant, call pop-up, page tracking).
  const platformOperator = isSuperuser(user);
  const homePath = homePathFor(user);

  // Track page visits for personalized quick actions (tenant dashboards only)
  usePageTracker({ enabled: !platformOperator, user });
  
  // Refs for detecting outside clicks
  const profileMenuRef = useRef(null);
  const modulesMenuRef = useRef(null);
  const mobileMenuRef = useRef(null);
  const mobileMenuButtonRef = useRef(null);

  useEffect(() => {
    if (user && !platformOperator) {
      fetchUserModules();
    } else {
      // The platform has no modules (/api/modules/my is [] for the superuser).
      setUserModules([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]); // Re-fetch when user changes

  // Handle outside clicks to close menus
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close profile menu if clicked outside
      if (profileMenuRef.current && !profileMenuRef.current.contains(event.target)) {
        setProfileMenuOpen(false);
      }
      // Close modules menu if clicked outside
      if (modulesMenuRef.current && !modulesMenuRef.current.contains(event.target)) {
        setModulesMenuOpen(false);
      }
      // Close mobile menu if clicked outside (but not on the button itself)
      if (mobileMenuOpen && 
          mobileMenuRef.current && 
          !mobileMenuRef.current.contains(event.target) &&
          mobileMenuButtonRef.current &&
          !mobileMenuButtonRef.current.contains(event.target)) {
        setMobileMenuOpen(false);
      }
    };

    // Add event listener
    document.addEventListener('mousedown', handleClickOutside);
    
    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [mobileMenuOpen]);

  const fetchUserModules = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API_URL}/api/modules/my`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });
      setUserModules(response.data);
    } catch (error) {
      console.error('Failed to fetch modules:', error);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const main = (
    // F20: room under the last row on phones, so the floating assistant
    // button (bottom right) does not cover the last card's actions
    <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-[calc(6rem+env(safe-area-inset-bottom))] sm:pb-8">
      {/* OFF-16: a screen the user has no right to is not drawn at all */}
      {canOpenPath(user, location.pathname) ? children : <PermissionDenied />}
    </main>
  );

  const shell = (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-100 transition-colors">
      <nav className="bg-white dark:bg-gray-800 shadow-md">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex items-center min-w-0">
              <Link to={homePath} className="flex items-center shrink-0">
                <Logo width={120} height={36} adaptive />
              </Link>
              
              {/* Desktop Navigation */}
              <div className="hidden md:flex md:ms-10 md:gap-4">
                {platformOperator ? (
                  <Link
                    to={homePath}
                    className="text-gray-700 dark:text-gray-200 hover:text-primary-600 dark:hover:text-primary-400 px-3 py-2 rounded-md text-sm font-medium transition"
                  >
                    {t('dashboard.superuserPanel')}
                  </Link>
                ) : (
                  <Link
                    to="/dashboard"
                    className="text-gray-700 dark:text-gray-200 hover:text-primary-600 dark:hover:text-primary-400 px-3 py-2 rounded-md text-sm font-medium transition"
                  >
                    {t('navigation.dashboard')}
                  </Link>
                )}
                
                {platformOperator && (
                  <>
                    <Link
                      to="/companies"
                      className="text-gray-700 dark:text-gray-200 hover:text-primary-600 dark:hover:text-primary-400 px-3 py-2 rounded-md text-sm font-medium transition"
                    >
                      {t('navigation.companies')}
                    </Link>
                    <Link
                      to="/licenses"
                      className="text-gray-700 dark:text-gray-200 hover:text-primary-600 dark:hover:text-primary-400 px-3 py-2 rounded-md text-sm font-medium transition"
                    >
                      {t('navigation.licenses')}
                    </Link>
                    <Link
                      to="/system-health"
                      className="text-gray-700 dark:text-gray-200 hover:text-primary-600 dark:hover:text-primary-400 px-3 py-2 rounded-md text-sm font-medium transition"
                    >
                      {t('navigation.systemHealth')}
                    </Link>
                  </>
                )}
                
                {isAdmin(user) && (
                  <Link
                    to="/users"
                    className="text-gray-700 dark:text-gray-200 hover:text-primary-600 dark:hover:text-primary-400 px-3 py-2 rounded-md text-sm font-medium transition"
                  >
                    {t('navigation.users')}
                  </Link>
                )}

                {/* Modules Dropdown */}
                {!platformOperator && userModules.length > 0 && (
                  <div className="relative" ref={modulesMenuRef}>
                    <button
                      onClick={() => setModulesMenuOpen(!modulesMenuOpen)}
                      className="flex items-center gap-1 text-gray-700 dark:text-surface-200 hover:text-primary-600 px-3 py-2 rounded-md text-sm font-medium transition"
                    >
                      <span>{t('navigation.modules')}</span>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>

                    {modulesMenuOpen && (
                      <div className="absolute start-0 mt-2 w-64 bg-white dark:bg-gray-800 rounded-lg shadow-lg py-1 z-50 max-h-[500px] overflow-y-auto">
                        {userModules.map((module) => (
                          <Link
                            key={module.id}
                            to={`/modules/${module.id}`}
                            onClick={() => setModulesMenuOpen(false)}
                            className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:bg-surface-800 dark:hover:bg-gray-700"
                          >
                            {module.icon} {moduleDisplayName({ ...module, key: module.id }, t)}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* R5-PS-10: at 390 px the icons pushed the page 18 px wide; the
                theme switch moves into the menu on phones */}
            <div className="flex items-center gap-0.5 sm:gap-4 shrink-0">
              {/* OFF-25: global search (Ctrl+K) and notification bell (company users) */}
              {!platformOperator && <GlobalSearch />}
              {!platformOperator && <ApprovalsBadge />}
              {!platformOperator && <NotificationBell />}

              {/* Language Switcher (grouped, searchable; components/LanguageSwitcher) */}
              <LanguageSwitcher variant="app" />

              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                aria-label={theme === 'dark' ? t('shell.layout.switchToLight') : t('shell.layout.switchToDark')}
                title={theme === 'dark' ? t('shell.layout.switchToLight') : t('shell.layout.switchToDark')}
                className="hidden sm:block p-2 rounded-md text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:bg-surface-800 dark:hover:bg-gray-700 transition"
              >
                {theme === 'dark' ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364-6.364l-.707.707M6.343 17.657l-.707.707m12.728 0l-.707-.707M6.343 6.343l-.707-.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                  </svg>
                )}
              </button>

              {/* Profile Dropdown */}
              <div className="relative hidden md:block" ref={profileMenuRef}>
                <button
                  onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                  className="flex items-center gap-2 text-gray-700 dark:text-surface-200 hover:text-primary-600 px-3 py-2 rounded-md text-sm font-medium transition"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <span className="font-semibold">{user?.username}</span>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {profileMenuOpen && (
                  <div className="absolute end-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg py-1 z-50">
                    <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                      <p className="text-sm text-gray-500 dark:text-gray-400">{t('shell.layout.signedInAs')}</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{user?.username}</p>
                      {user?.company_name && (
                        <p className="text-xs text-gray-500 dark:text-gray-400">{user?.company_name}</p>
                      )}
                    </div>
                    {!platformOperator && (
                      <Link
                        to="/approvals"
                        onClick={() => setProfileMenuOpen(false)}
                        className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:bg-surface-800 dark:hover:bg-gray-700"
                      >
                        {t('approvals.menu')}
                      </Link>
                    )}
                    <Link
                      to="/settings"
                      onClick={() => setProfileMenuOpen(false)}
                      className="block px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:bg-surface-800 dark:hover:bg-gray-700"
                    >
                      {t('navigation.settings')}
                    </Link>
                    <button
                      onClick={handleLogout}
                      className="w-full text-start px-4 py-2 text-sm text-red-700 hover:bg-red-50"
                    >
                      {t('auth.logout')}
                    </button>
                  </div>
                )}
              </div>
              
              {/* Mobile menu button */}
              <button
                ref={mobileMenuButtonRef}
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                aria-label={t('shell.layout.menu')}
                className="md:hidden p-2 rounded-md text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:bg-surface-800 dark:hover:bg-gray-700"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div ref={mobileMenuRef} className="md:hidden border-t border-gray-200 dark:border-gray-700">
            <div className="px-2 pt-2 pb-3 space-y-1">
              <Link
                to={homePath}
                onClick={() => setMobileMenuOpen(false)}
                className="block text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:bg-surface-800 dark:hover:bg-gray-700 px-3 py-2 rounded-md text-base font-medium"
              >
                {platformOperator
                  ? t('dashboard.superuserPanel')
                  : t('navigation.dashboard')}
              </Link>
              
              {platformOperator && (
                <>
                  <Link
                    to="/companies"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:bg-surface-800 dark:hover:bg-gray-700 px-3 py-2 rounded-md text-base font-medium"
                  >
                    {t('navigation.companies')}
                  </Link>
                  <Link
                    to="/licenses"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:bg-surface-800 dark:hover:bg-gray-700 px-3 py-2 rounded-md text-base font-medium"
                  >
                    {t('navigation.licenses')}
                  </Link>
                  <Link
                    to="/system-health"
                    onClick={() => setMobileMenuOpen(false)}
                    className="block text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:bg-surface-800 dark:hover:bg-gray-700 px-3 py-2 rounded-md text-base font-medium"
                  >
                    {t('navigation.systemHealth')}
                  </Link>
                </>
              )}
              
              {isAdmin(user) && (
                <Link
                  to="/users"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:bg-surface-800 dark:hover:bg-gray-700 px-3 py-2 rounded-md text-base font-medium"
                >
                  {t('navigation.users')}
                </Link>
              )}
              
              {/* Modules for Mobile */}
              {!platformOperator && userModules.length > 0 && (
                <div className="border-t border-gray-200 dark:border-gray-700 mt-2 pt-2">
                  <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-surface-400 uppercase">
                    {t('navigation.modules')}
                  </div>
                  {userModules.map((module) => (
                    <Link
                      key={module.id}
                      to={`/modules/${module.id}`}
                      onClick={() => setMobileMenuOpen(false)}
                      className="block text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:bg-surface-800 dark:hover:bg-gray-700 px-3 py-2 rounded-md text-base font-medium"
                    >
                      {module.icon} {moduleDisplayName({ ...module, key: module.id }, t)}
                    </Link>
                  ))}
                </div>
              )}
              
              {!platformOperator && (
                <Link
                  to="/approvals"
                  onClick={() => setMobileMenuOpen(false)}
                  className="block text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:bg-surface-800 dark:hover:bg-gray-700 px-3 py-2 rounded-md text-base font-medium"
                >
                  {t('approvals.menu')}
                </Link>
              )}

              <Link
                to="/settings"
                onClick={() => setMobileMenuOpen(false)}
                className="block text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:bg-surface-800 dark:hover:bg-gray-700 px-3 py-2 rounded-md text-base font-medium"
              >
                {t('navigation.settings')}
              </Link>

              <button
                type="button"
                onClick={() => {
                  setMobileMenuOpen(false);
                  toggleTheme();
                }}
                data-testid="mobile-theme-toggle"
                className="sm:hidden w-full text-start text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 px-3 py-2 rounded-md text-base font-medium"
              >
                {theme === 'dark' ? t('shell.layout.switchToLight') : t('shell.layout.switchToDark')}
              </button>

              <button
                onClick={() => {
                  setMobileMenuOpen(false);
                  handleLogout();
                }}
                className="w-full text-start text-red-700 hover:bg-red-50 px-3 py-2 rounded-md text-base font-medium"
              >
                {t('auth.logout')}
              </button>
            </div>
          </div>
        )}
      </nav>

      {platformOperator ? main : <LicenseGuard>{main}</LicenseGuard>}

      {!platformOperator && (
        <>
          {/* AI Chat Widget */}
          <AIChatWidget />

          {/* Netgsm santral: incoming-call pop-up (no-op unless enabled) */}
          <IncomingCallWatcher />

          {/* AI phone assistant: pop-up for calls it answered (no-op unless enabled) */}
          <AiCallAlerts />

          {/* Netgsm santral: in-browser phone (no-op without an extension password) */}
          <SoftphonePanel />
        </>
      )}
    </div>
  );

  // Tenant-only widgets (licence banner, AI assistant, softphone, call
  // pop-up) and the softphone's SIP registration are not mounted for the
  // platform operator: it has no company, so they only produced 403s
  // ("A concrete tenant_id is required") and error toasts.
  return platformOperator ? shell : <SoftphoneProvider>{shell}</SoftphoneProvider>;
};